import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { CreditCard, Check, XCircle, ArrowUpRight } from 'lucide-react';
import { Button } from '../ui/button';
import { saasApi } from '../../api/client';
import { useToast } from '../../hooks/use-toast';

export const SubscriptionTab = () => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const { toast } = useToast();

  const load = () => saasApi.subscription().then(setData).catch(() => {});
  useEffect(() => { load(); }, []);

  if (!data) return <div className="text-[#52676e] text-sm py-8 text-center">Carregando assinatura...</div>;

  const { plan, subscription, entitlements } = data;

  const cancel = async () => {
    setBusy(true);
    try {
      const res = await saasApi.cancelSubscription();
      toast({ title: 'Assinatura cancelada', description: res.message });
      load();
    } catch (err) {
      toast({ title: 'Erro', description: err.response?.data?.detail || 'Falha ao cancelar.', variant: 'destructive' });
    } finally { setBusy(false); }
  };

  const entLabels = {
    profile_verified: 'Selo verificado', ai_receptionist: 'Recepcionista com IA', crm_enabled: 'CRM',
    automations_enabled: 'Automações', site_builder_enabled: 'Site próprio',
  };

  return (
    <div className="space-y-4" data-testid="subscription-tab">
      <div className="bg-white border border-[#cbd8dc] rounded-[10px] p-6">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <div className="text-xs font-semibold text-[#075a6d] uppercase tracking-widest flex items-center gap-1.5"><CreditCard className="w-3.5 h-3.5" /> Plano atual</div>
            <h3 className="text-2xl font-semibold text-[#153e44] mt-1" data-testid="current-plan-name">{plan.name}</h3>
            <p className="text-sm text-[#52676e]">{plan.price === 0 ? 'Gratuito' : `R$${plan.price.toLocaleString('pt-BR')}/mês`} • {plan.tagline}</p>
            {subscription && (
              <p className="text-xs text-[#52676e] mt-1">
                Status: <span className={`font-semibold ${subscription.status === 'active' ? 'text-emerald-600' : 'text-amber-600'}`}>{subscription.status === 'active' ? 'Ativa' : subscription.status === 'canceled' ? `Cancelada — benefícios até ${subscription.periodEnd?.slice(0, 10)}` : subscription.status}</span>
              </p>
            )}
          </div>
          <div className="flex gap-2">
            <Link to="/planos"><Button className="bg-[#153e44] hover:bg-[#0c3036]" data-testid="upgrade-plan-btn">Mudar de plano <ArrowUpRight className="w-4 h-4 ml-1.5" /></Button></Link>
            {subscription && subscription.status === 'active' && (
              <Button variant="outline" onClick={cancel} disabled={busy} className="text-red-600 border-red-200 hover:bg-red-50" data-testid="cancel-subscription-btn">
                <XCircle className="w-4 h-4 mr-1.5" /> Cancelar
              </Button>
            )}
          </div>
        </div>
      </div>

      <div className="bg-white border border-[#cbd8dc] rounded-[10px] p-6">
        <h4 className="font-semibold text-[#153e44] mb-3">Recursos do seu plano</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {Object.entries(entLabels).map(([k, label]) => (
            <div key={k} className={`flex items-center gap-2 text-sm px-3 py-2 rounded-[7px] ${entitlements[k] ? 'bg-emerald-50 text-emerald-800' : 'bg-[#f7fafb] text-[#6b858e]'}`}>
              {entitlements[k] ? <Check className="w-4 h-4" /> : <XCircle className="w-4 h-4" />} {label}
            </div>
          ))}
          <div className="flex items-center gap-2 text-sm px-3 py-2 rounded-[7px] bg-[#f7fafb] text-[#425a62]">Cidades: até {entitlements.max_locations >= 999 ? 'ilimitadas' : entitlements.max_locations}</div>
          <div className="flex items-center gap-2 text-sm px-3 py-2 rounded-[7px] bg-[#f7fafb] text-[#425a62]">Áreas: até {entitlements.max_specialties >= 999 ? 'ilimitadas' : entitlements.max_specialties}</div>
        </div>
      </div>
      <p className="text-xs text-[#6b858e]">Cobrança em modo demonstração — nenhum valor real é processado.</p>
    </div>
  );
};
